import { useEffect, useMemo, useState } from "react";
import { Check, Copy, Minus, Plus, Share2, ShoppingBasket, Trash2, UsersRound, X } from "lucide-react";
import { useI18n } from "./i18n";
import {
  buildShoppingList,
  createShareText,
  formatShoppingAmount,
  SHOPPING_CATEGORY_ORDER,
  shoppingItemId,
} from "./shopping";
import type { Recipe, ShoppingCategory, ShoppingSelection } from "./types";

const categoryFallbacks: Record<ShoppingCategory, string> = {
  produce: "Obst & Gemüse",
  chilled: "Kühlregal",
  legumes: "Hülsenfrüchte",
  pantry: "Vorrat",
  spices: "Gewürze",
  toppings: "Kerne & Extras",
};

function copyFallback(text: string): boolean {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const success = document.execCommand("copy");
  textarea.remove();
  return success;
}

export function PeopleStepper({
  value,
  onChange,
  compact = false,
}: {
  value: number;
  onChange: (people: number) => void;
  compact?: boolean;
}) {
  const t = useI18n();
  return (
    <div className={`people-stepper ${compact ? "compact" : ""}`} aria-label={t("shopping.people", "Personenzahl")}>
      <button onClick={() => onChange(Math.max(1, value - 1))} disabled={value <= 1} aria-label={t("shopping.peopleLess", "Eine Person weniger")}><Minus /></button>
      <span><UsersRound /> <b>{value}</b><small>{value === 1 ? t("shopping.person", "Person") : t("shopping.peoplePlural", "Personen")}</small></span>
      <button onClick={() => onChange(Math.min(12, value + 1))} disabled={value >= 12} aria-label={t("shopping.peopleMore", "Eine Person mehr")}><Plus /></button>
    </div>
  );
}

export function ShoppingPlanner({
  recipes,
  selections,
  checkedIds,
  onPeopleChange,
  onRemoveRecipe,
  onToggleItem,
  onClearChecked,
  onClearAll,
  onClose,
}: {
  recipes: Recipe[];
  selections: ShoppingSelection[];
  checkedIds: Set<string>;
  onPeopleChange: (slug: string, people: number) => void;
  onRemoveRecipe: (slug: string) => void;
  onToggleItem: (id: string) => void;
  onClearChecked: () => void;
  onClearAll: () => void;
  onClose: () => void;
}) {
  const t = useI18n();
  const [feedback, setFeedback] = useState("");
  const items = useMemo(() => buildShoppingList(recipes, selections), [recipes, selections]);
  const completed = items.filter((item) => checkedIds.has(shoppingItemId(item))).length;
  const progress = items.length ? Math.round((completed / items.length) * 100) : 0;

  useEffect(() => {
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, []);

  const copyList = async () => {
    const text = createShareText(items, checkedIds);
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
      else if (!copyFallback(text)) throw new Error("copy failed");
      setFeedback(t("shopping.copied", "Liste kopiert"));
    } catch {
      setFeedback(t("shopping.copyFailed", "Kopieren war nicht möglich"));
    }
    window.setTimeout(() => setFeedback(""), 2200);
  };

  const shareList = async () => {
    const text = createShareText(items, checkedIds);
    if (!navigator.share) return copyList();
    try {
      await navigator.share({ title: t("shopping.title", "Einkaufsliste"), text });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      await copyList();
    }
  };

  return (
    <div className="planner-backdrop" role="presentation">
      <section className="shopping-planner" role="dialog" aria-modal="true" aria-labelledby="shopping-title">
        <header className="planner-header">
          <div className="planner-mark"><ShoppingBasket /></div>
          <div><p className="eyebrow">{t("shopping.eyebrow", "Dein Menü im Überblick")}</p><h1 id="shopping-title">{t("shopping.title", "Einkaufsliste")}</h1></div>
          <button className="planner-close" onClick={onClose} aria-label={t("shopping.close", "Einkaufsliste schließen")}><X /></button>
        </header>

        <div className="planner-content">
          <section className="planned-recipes" aria-labelledby="planned-title">
            <div className="planner-section-heading"><div><p className="eyebrow">{t("shopping.menu", "Ausgewählte Rezepte")}</p><h2 id="planned-title">{t("shopping.planTitle", "Für wen kochst du?")}</h2></div><button className="plain-danger" onClick={onClearAll}><Trash2 /> {t("shopping.clearPlan", "Plan leeren")}</button></div>
            <div className="planned-list">
              {selections.map((selection) => {
                const recipe = recipes.find((candidate) => candidate.slug === selection.recipeSlug);
                if (!recipe) return null;
                return (
                  <article key={recipe.slug}>
                    <img src={recipe.image} alt="" />
                    <div className="planned-copy"><h3>{recipe.title}</h3><p>{t("shopping.baseServings", "Grundrezept: {count} Portionen", { count: recipe.servings })}</p></div>
                    <PeopleStepper value={selection.people} onChange={(people) => onPeopleChange(recipe.slug, people)} compact />
                    <button className="remove-recipe" onClick={() => onRemoveRecipe(recipe.slug)} aria-label={t("shopping.removeRecipe", "{recipe} entfernen", { recipe: recipe.title })}><X /></button>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="grocery-list" aria-labelledby="grocery-title">
            <div className="grocery-summary">
              <div><p className="eyebrow">{t("shopping.generated", "Automatisch zusammengeführt")}</p><h2 id="grocery-title">{t("shopping.itemsTitle", "Das brauchst du")}</h2></div>
              <div className="shopping-progress" aria-label={t("shopping.progress", "{done} von {total} erledigt", { done: completed, total: items.length })}>
                <span><i style={{ width: `${progress}%` }} /></span><b>{completed}/{items.length}</b>
              </div>
            </div>

            {SHOPPING_CATEGORY_ORDER.map((category) => {
              const categoryItems = items.filter((item) => item.category === category).sort((left, right) => Number(checkedIds.has(shoppingItemId(left))) - Number(checkedIds.has(shoppingItemId(right))));
              if (!categoryItems.length) return null;
              return (
                <div className="grocery-category" key={category}>
                  <h3>{t(`shopping.category.${category}`, categoryFallbacks[category])}</h3>
                  <div className="grocery-items">
                    {categoryItems.map((item) => {
                      const id = shoppingItemId(item);
                      const checked = checkedIds.has(id);
                      return (
                        <button key={id} className={checked ? "checked" : ""} onClick={() => onToggleItem(id)} role="checkbox" aria-checked={checked}>
                          <i>{checked && <Check />}</i>
                          <span><b>{item.name}</b><small>{formatShoppingAmount(item.amount, item.unit)}{item.note ? ` · ${item.note}` : ""}</small></span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <div className="planner-actions">
              <button className="secondary-button" onClick={onClearChecked} disabled={!completed}><Check /> {t("shopping.resetChecked", "Erledigte zurücksetzen")}</button>
              <button className="secondary-button" onClick={copyList}><Copy /> {t("shopping.copy", "Kopieren")}</button>
              <button className="primary-button" onClick={shareList}><Share2 /> {t("shopping.share", "Liste teilen")}</button>
            </div>
            <p className="shopping-hint">{t("shopping.hint", "Mengen werden proportional berechnet. Bei Stückware wird für den Einkauf auf ganze Stücke aufgerundet.")}</p>
            <span className="copy-feedback" aria-live="polite">{feedback}</span>
          </section>
        </div>
      </section>
    </div>
  );
}
